import 'maplibre-gl/dist/maplibre-gl.css';

import {
    type GeoJSONSource,
    type LngLatBounds,
    type Map as MaplibreMap,
    type MapLayerMouseEvent,
    type Marker,
    type MarkerOptions,
    type Popup,
    type PopupOptions,
} from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Switch } from '@apps/web/components/ui/switch';
import { type RequestStatus } from '@apps/web/features/coverage/model/request_model';
import {
    type CoverageClientDensityStatus,
    type CoverageMapStatus,
} from '@apps/web/features/coverage/model/use_coverage_map';
import {
    type PotentialClientPin,
    PotentialClientSearchCard,
} from '@apps/web/features/coverage/ui/potential_client_search_card';
import {
    configureMaplibreWorker,
    MAP_STYLE,
    OFFICE_COLORS,
    UNASSIGNED_OFFICE_COLOR,
} from '@apps/web/lib/map_style';
import {
    type WorkforceMapClientDensityCell,
    type WorkforceMapPoint,
} from '@libraries/typescript/api_client';

type MaplibreConstructors = {
    readonly LngLatBounds: new () => LngLatBounds;
    readonly Marker: new (options?: MarkerOptions) => Marker;
    readonly Popup: new (options?: PopupOptions) => Popup;
};

const POINTS_SOURCE_ID = 'coverage-caregivers';
const CLIENT_DENSITY_SOURCE_ID = 'coverage-client-density';
const CLIENT_DENSITY_FILL_LAYER_ID = 'coverage-client-density-fill';
const CLIENT_DENSITY_OUTLINE_LAYER_ID = 'coverage-client-density-outline';
const CLIENT_DENSITY_COLOR = '#7c3aed';
const POTENTIAL_CLIENT_COLOR = '#7c3aed';

function officeColorScale(
    points: ReadonlyArray<{ readonly office_name: string | null }>,
): Map<string, string> {
    const offices = [
        ...new Set(
            points
                .map((point) => point.office_name)
                .filter((office): office is string => !!office),
        ),
    ].sort((a, b) => a.localeCompare(b));
    return new Map(
        offices.map((office, index) => [
            office,
            OFFICE_COLORS[index % OFFICE_COLORS.length] ??
                UNASSIGNED_OFFICE_COLOR,
        ]),
    );
}

function densityOpacity(
    countBand: WorkforceMapClientDensityCell['count_band'],
): number {
    if (countBand === '20+') {
        return 0.5;
    }
    if (countBand === '10-19') {
        return 0.34;
    }
    return 0.2;
}

function potentialClientPinElement(): HTMLElement {
    const element = document.createElement('div');
    element.innerHTML =
        '<svg width="30" height="38" viewBox="0 0 30 38" aria-hidden="true">' +
        `<path d="M15 1C7.8 1 2 6.8 2 14c0 9.6 13 23 13 23s13-13.4 13-23C28 6.8 22.2 1 15 1z" fill="${POTENTIAL_CLIENT_COLOR}" stroke="#fff" stroke-width="2"/>` +
        '<circle cx="15" cy="14" r="4.5" fill="#fff"/></svg>';
    element.style.filter = 'drop-shadow(0 2px 3px rgb(0 0 0 / 0.3))';
    return element;
}

export function CoverageMapPane({
    status,
    errorMessage,
    refreshErrorMessage,
    onRetry,
    points,
    clientDensityCells,
    clientDensityStatus,
    clientsLayerEnabled,
    plotClients,
    onPlotClientsChange,
    organizationId,
    visibleCaregiverIds,
    coverageBanner,
    canRequestGeocode,
    geocodeStatus,
    geocodeError,
    onRequestGeocode,
    onDismissGeocodeError,
}: {
    readonly status: CoverageMapStatus;
    readonly errorMessage: string | null;
    readonly refreshErrorMessage: string | null;
    readonly onRetry: () => void;
    readonly points: ReadonlyArray<WorkforceMapPoint>;
    readonly clientDensityCells: ReadonlyArray<WorkforceMapClientDensityCell>;
    readonly clientDensityStatus: CoverageClientDensityStatus;
    readonly clientsLayerEnabled: boolean;
    readonly plotClients: boolean;
    readonly onPlotClientsChange: (plotClients: boolean) => void;
    readonly organizationId: string | null;
    readonly visibleCaregiverIds: ReadonlySet<string>;
    readonly coverageBanner: string;
    readonly canRequestGeocode: boolean;
    readonly geocodeStatus: RequestStatus;
    readonly geocodeError: string | null;
    readonly onRequestGeocode: () => Promise<void>;
    readonly onDismissGeocodeError: () => void;
}) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<MaplibreMap | null>(null);
    const maplibreConstructorsRef = useRef<MaplibreConstructors | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [hasRendererError, setHasRendererError] = useState(false);
    const [rendererAttempt, setRendererAttempt] = useState(0);
    const [potentialClient, setPotentialClient] =
        useState<PotentialClientPin | null>(null);
    const visiblePoints = useMemo(
        () =>
            points.filter((point) =>
                visibleCaregiverIds.has(point.caregiver_id),
            ),
        [points, visibleCaregiverIds],
    );
    const visibleClientDensityCells = useMemo(
        () => (plotClients ? clientDensityCells : []),
        [plotClients, clientDensityCells],
    );
    const colorByOffice = useMemo(
        () => officeColorScale(visiblePoints),
        [visiblePoints],
    );
    const visibleOffices = useMemo(
        () =>
            new Set(
                visiblePoints
                    .map((point) => point.office_name)
                    .filter((office): office is string => !!office),
            ),
        [visiblePoints],
    );
    const hasUnassignedOffice = visiblePoints.some(
        (point) => !point.office_name,
    );
    useEffect(() => {
        setPotentialClient(null);
    }, [organizationId]);

    useEffect(() => {
        if (status !== 'ready' || !containerRef.current) {
            return;
        }
        let cancelled = false;
        let failed = false;
        let map: MaplibreMap | null = null;
        const container = containerRef.current;
        setIsMapReady(false);
        setHasRendererError(false);

        const fail = () => {
            if (cancelled || failed) {
                return;
            }
            failed = true;
            map?.remove();
            map = null;
            mapRef.current = null;
            maplibreConstructorsRef.current = null;
            setIsMapReady(false);
            setHasRendererError(true);
        };

        import('maplibre-gl')
            .then((module) => {
                if (cancelled || !container.isConnected) {
                    return;
                }
                configureMaplibreWorker(module);
                maplibreConstructorsRef.current = {
                    LngLatBounds: module.LngLatBounds,
                    Marker: module.Marker,
                    Popup: module.Popup,
                };
                const createdMap = new module.Map({
                    attributionControl: { compact: false },
                    container,
                    style: MAP_STYLE,
                    center: [-98.5, 39.8],
                    zoom: 3,
                });
                map = createdMap;
                mapRef.current = createdMap;
                createdMap.addControl(
                    new module.FullscreenControl(),
                    'top-right',
                );
                createdMap.addControl(
                    new module.NavigationControl({ showCompass: false }),
                    'top-right',
                );
                createdMap.on('load', () => {
                    if (!cancelled && !failed) {
                        setIsMapReady(true);
                    }
                });
                createdMap.on('error', () => {
                    if (!createdMap.isStyleLoaded()) {
                        fail();
                    }
                });
            })
            .catch(fail);

        return () => {
            cancelled = true;
            map?.remove();
            mapRef.current = null;
            maplibreConstructorsRef.current = null;
        };
    }, [rendererAttempt, status]);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady) {
            return;
        }
        const featureCollection = {
            type: 'FeatureCollection' as const,
            features: visiblePoints.map((point) => ({
                type: 'Feature' as const,
                geometry: {
                    type: 'Point' as const,
                    coordinates: [point.longitude, point.latitude],
                },
                properties: {
                    name: point.name,
                    description: [
                        point.city,
                        point.postal_code,
                        point.office_name,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                    color: point.office_name
                        ? (colorByOffice.get(point.office_name) ??
                          UNASSIGNED_OFFICE_COLOR)
                        : UNASSIGNED_OFFICE_COLOR,
                },
            })),
        };
        const existing = map.getSource(POINTS_SOURCE_ID);
        if (existing && 'setData' in existing) {
            (existing as GeoJSONSource).setData(featureCollection);
        } else {
            let pointPopup: Popup | null = null;
            const showPointPopup = (event: MapLayerMouseEvent) => {
                const feature = event.features?.[0];
                if (!feature || feature.geometry.type !== 'Point') {
                    return;
                }
                const [longitude, latitude] = feature.geometry.coordinates;
                const content = document.createElement('div');
                const nameLine = document.createElement('strong');
                nameLine.textContent = String(
                    feature.properties?.['name'] ?? '',
                );
                const descriptionLine = document.createElement('div');
                descriptionLine.textContent = String(
                    feature.properties?.['description'] ?? '',
                );
                content.append(nameLine, descriptionLine);
                pointPopup?.remove();
                pointPopup = new constructors.Popup({ closeButton: false })
                    .setLngLat([longitude ?? 0, latitude ?? 0])
                    .setDOMContent(content)
                    .addTo(map);
            };
            map.addSource(POINTS_SOURCE_ID, {
                type: 'geojson',
                data: featureCollection,
            });
            map.addLayer({
                id: POINTS_SOURCE_ID,
                type: 'circle',
                source: POINTS_SOURCE_ID,
                paint: {
                    'circle-radius': 6,
                    'circle-color': ['get', 'color'],
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': '#ffffff',
                },
            });
            map.on('mousemove', POINTS_SOURCE_ID, showPointPopup);
            map.on('click', POINTS_SOURCE_ID, showPointPopup);
            map.on('mouseenter', POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = 'pointer';
            });
            map.on('mouseleave', POINTS_SOURCE_ID, () => {
                pointPopup?.remove();
                pointPopup = null;
                map.getCanvas().style.cursor = '';
            });
        }

        // These polygons are fixed H3 cells with at least five source clients.
        // No source coordinate or exact count is present in browser memory.
        const clientDensityFeatureCollection = {
            type: 'FeatureCollection' as const,
            features: visibleClientDensityCells.map((cell) => ({
                type: 'Feature' as const,
                geometry: {
                    type: 'Polygon' as const,
                    coordinates: [
                        cell.boundary.map((vertex) => [
                            vertex.longitude,
                            vertex.latitude,
                        ]),
                    ],
                },
                properties: {
                    opacity: densityOpacity(cell.count_band),
                },
            })),
        };
        const existingDensity = map.getSource(CLIENT_DENSITY_SOURCE_ID);
        if (existingDensity && 'setData' in existingDensity) {
            (existingDensity as GeoJSONSource).setData(
                clientDensityFeatureCollection,
            );
        } else {
            map.addSource(CLIENT_DENSITY_SOURCE_ID, {
                type: 'geojson',
                data: clientDensityFeatureCollection,
            });
            map.addLayer(
                {
                    id: CLIENT_DENSITY_FILL_LAYER_ID,
                    type: 'fill',
                    source: CLIENT_DENSITY_SOURCE_ID,
                    paint: {
                        'fill-color': CLIENT_DENSITY_COLOR,
                        'fill-opacity': ['get', 'opacity'],
                    },
                },
                POINTS_SOURCE_ID,
            );
            map.addLayer(
                {
                    id: CLIENT_DENSITY_OUTLINE_LAYER_ID,
                    type: 'line',
                    source: CLIENT_DENSITY_SOURCE_ID,
                    paint: {
                        'line-color': CLIENT_DENSITY_COLOR,
                        'line-opacity': 0.65,
                        'line-width': 1,
                    },
                },
                POINTS_SOURCE_ID,
            );
        }

        const densityVertices = visibleClientDensityCells.flatMap(
            (cell) => cell.boundary,
        );
        if (visiblePoints.length > 0 || densityVertices.length > 0) {
            const bounds = new constructors.LngLatBounds();
            for (const point of visiblePoints) {
                bounds.extend([point.longitude, point.latitude]);
            }
            for (const vertex of densityVertices) {
                bounds.extend([vertex.longitude, vertex.latitude]);
            }
            map.fitBounds(bounds, { maxZoom: 10, padding: 48 });
        }
    }, [visiblePoints, visibleClientDensityCells, colorByOffice, isMapReady]);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady || !potentialClient) {
            return;
        }
        const marker = new constructors.Marker({
            anchor: 'bottom',
            element: potentialClientPinElement(),
        })
            .setLngLat([potentialClient.longitude, potentialClient.latitude])
            .addTo(map);
        return () => {
            marker.remove();
        };
    }, [potentialClient, isMapReady]);

    const plotPotentialClient = useCallback((pin: PotentialClientPin) => {
        setPotentialClient(pin);
        mapRef.current?.flyTo({
            center: [pin.longitude, pin.latitude],
            zoom: 11,
        });
    }, []);

    const selectCaregiver = useCallback((point: WorkforceMapPoint) => {
        mapRef.current?.flyTo({
            center: [point.longitude, point.latitude],
            zoom: 12,
        });
    }, []);

    if (status === 'idle') {
        return null;
    }
    if (status === 'loading') {
        return (
            <div className="space-y-3" aria-label="Loading caregiver locations">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-[520px] w-full rounded-xl" />
            </div>
        );
    }
    if (status === 'error') {
        return (
            <div className="py-10 text-center">
                <p className="text-muted-foreground text-sm">
                    {errorMessage ?? 'We couldn’t load caregiver locations.'}
                </p>
                <Button variant="outline" className="mt-3" onClick={onRetry}>
                    Try again
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {refreshErrorMessage ? (
                <div
                    className="border-border bg-muted/50 flex items-center gap-2 rounded-lg border px-3 py-2"
                    role="alert"
                >
                    <p className="text-muted-foreground grow text-sm">
                        {refreshErrorMessage}
                    </p>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                        Try again
                    </Button>
                </div>
            ) : null}
            <div className="bg-muted text-muted-foreground flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-xs">
                <p className="grow">{coverageBanner}</p>
                {canRequestGeocode ? (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={geocodeStatus === 'pending'}
                        onClick={() => void onRequestGeocode()}
                    >
                        {geocodeStatus === 'pending'
                            ? 'Starting update…'
                            : 'Update locations'}
                    </Button>
                ) : null}
            </div>
            {geocodeStatus === 'success' ? (
                <p className="text-sm text-emerald-700" role="status">
                    Location update queued. Refresh the map after processing
                    completes.
                </p>
            ) : null}
            {geocodeStatus === 'error' ? (
                <div className="flex items-center gap-2" role="alert">
                    <p className="text-destructive grow text-sm">
                        {geocodeError ??
                            'We couldn’t start the location update.'}
                    </p>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDismissGeocodeError}
                    >
                        Dismiss
                    </Button>
                </div>
            ) : null}
            {plotClients &&
            clientDensityStatus === 'ready' &&
            clientDensityCells.length === 0 ? (
                <p
                    className="border-border bg-muted/50 text-muted-foreground rounded-lg border px-3 py-2 text-sm"
                    role="status"
                >
                    No client density is shown. Client locations stay hidden
                    until at least 5 active clients share the same
                    privacy-protected area.
                </p>
            ) : null}
            {plotClients && clientDensityStatus === 'loading' ? (
                <p
                    className="bg-muted/50 text-muted-foreground rounded-lg px-3 py-2 text-sm"
                    role="status"
                >
                    Loading client density…
                </p>
            ) : null}
            {plotClients && clientDensityStatus === 'error' ? (
                <p
                    className="border-destructive/30 bg-destructive/5 text-destructive rounded-lg border px-3 py-2 text-sm"
                    role="alert"
                >
                    We couldn’t load client density. Turn the layer off and on
                    to try again.
                </p>
            ) : null}
            {hasRendererError ? (
                <div className="py-10 text-center">
                    <p className="text-muted-foreground text-sm">
                        We couldn’t render the map. Check your connection and
                        try again.
                    </p>
                    <Button
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                            setHasRendererError(false);
                            setRendererAttempt((value) => value + 1);
                        }}
                    >
                        Try again
                    </Button>
                </div>
            ) : (
                <div className="relative">
                    {!isMapReady ? (
                        <Skeleton className="absolute inset-0 z-10 rounded-xl" />
                    ) : null}
                    {isMapReady && visiblePoints.length === 0 ? (
                        <p className="bg-background/80 text-muted-foreground absolute inset-x-0 top-4 z-10 mx-auto w-fit rounded-md px-3 py-1.5 text-sm">
                            No mapped caregivers match the current filters.
                        </p>
                    ) : null}
                    {isMapReady && clientsLayerEnabled ? (
                        <div className="border-border bg-background/95 absolute top-3 left-3 z-10 flex items-center gap-2 rounded-lg border px-3 py-2 shadow-sm">
                            <Switch
                                id="plot-clients"
                                checked={plotClients}
                                onCheckedChange={onPlotClientsChange}
                            />
                            <label
                                htmlFor="plot-clients"
                                className="text-xs font-medium"
                            >
                                Client density
                            </label>
                        </div>
                    ) : null}
                    {isMapReady && clientsLayerEnabled ? (
                        <div className="absolute bottom-3 left-3 z-10">
                            <PotentialClientSearchCard
                                organizationId={organizationId}
                                points={visiblePoints}
                                colorByOffice={colorByOffice}
                                unassignedOfficeColor={UNASSIGNED_OFFICE_COLOR}
                                plotted={potentialClient}
                                onPlotted={plotPotentialClient}
                                onClear={() => setPotentialClient(null)}
                                onSelectCaregiver={selectCaregiver}
                            />
                        </div>
                    ) : null}
                    <div
                        ref={containerRef}
                        className="h-[520px] w-full rounded-xl border"
                        role="application"
                        aria-label="Map of caregiver locations and client density"
                    />
                </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <span
                        className="bg-foreground size-2.5 rounded-full"
                        aria-hidden="true"
                    />
                    Caregiver
                </span>
                {plotClients && clientDensityCells.length > 0 ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <span
                            className="size-2.5 rounded-sm bg-violet-600/40 ring-1 ring-violet-600/70"
                            aria-hidden="true"
                        />
                        Client density (5+ per area)
                    </span>
                ) : null}
                {potentialClient ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <span
                            className="size-2.5 rounded-full bg-violet-600"
                            aria-hidden="true"
                        />
                        Searched address (temporary)
                    </span>
                ) : null}
                <span className="bg-border h-3.5 w-px" aria-hidden="true" />
                {[...colorByOffice.entries()]
                    .filter(([office]) => visibleOffices.has(office))
                    .map(([office, color]) => (
                        <span
                            key={office}
                            className="text-muted-foreground flex items-center gap-1.5 text-xs"
                        >
                            <span
                                className="size-2.5 rounded-full"
                                style={{ backgroundColor: color }}
                                aria-hidden="true"
                            />
                            {office}
                        </span>
                    ))}
                {hasUnassignedOffice ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <span
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: UNASSIGNED_OFFICE_COLOR }}
                            aria-hidden="true"
                        />
                        Unassigned office
                    </span>
                ) : null}
            </div>
        </div>
    );
}
